import React, { useState } from 'react';
import {
    Box,
    Select,
    MenuItem,
    IconButton,
    Button,
    Stack,
    FormControl,
    InputLabel,
    Typography,
    Grid,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    TextField,
    FormControlLabel,
    Radio,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

// Reusable field renderer
const renderField = (fieldConfig, value, onChange, isMainCondition = false) => {
    const labelColor = isMainCondition ? { color: 'white' } : {};

    switch (fieldConfig.type) {
        case "select":
            return (
                <FormControl fullWidth size="small">
                    <InputLabel sx={labelColor}>{fieldConfig.label}</InputLabel>
                    <Select
                        size="small"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        required={fieldConfig.required}
                    >
                        {fieldConfig.options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );

        case "number":
            return (
                <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={fieldConfig.label}
                    inputProps={{ min: fieldConfig.min || 0 }}
                    value={value || ''}
                    onChange={(e) => onChange(parseInt(e.target.value) || 0)}
                    required={fieldConfig.required}
                />
            );

        case "radio":
            console.log(fieldConfig);

            return (
                <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {fieldConfig.label}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                        {fieldConfig.options.map((op) => (
                            <FormControlLabel
                                key={op.value}
                                control={
                                    <Radio
                                        checked={value === op.value}
                                        onChange={() => onChange(op.value)}
                                        value={op.value}
                                    />
                                }
                                label={op.label}
                            />
                        ))}
                    </Box>
                </Box>
            );

        case "text":
            return (
                <TextField
                    fullWidth
                    size="small"
                    label={fieldConfig.label}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    required={fieldConfig.required}
                />
            );

        default:
            return <Typography>Unknown field type: {fieldConfig.type}</Typography>;
    }
};

const ConditionRow = ({ condition, onUpdate, onDelete, conditionTypes }) => {
    const currentConditionType = conditionTypes[condition.type];

    return (
        <Grid container spacing={2} direction="column">
            {/* Top Row: Condition Type, Value, Toggle */}
            <Grid container item spacing={2} alignItems="center">
                {/* Condition Type Select */}
                <Grid item xs={4}>
                    {renderField(
                        {
                            type: "select",
                            label: "Condition",
                            options: Object.entries(conditionTypes).map(([key, type]) => ({
                                value: key,
                                label: type.label
                            }))
                        },
                        condition.type,
                        (value) => onUpdate({ ...condition, type: value, value: '', subConditions: {} }),
                        true // isMainCondition
                    )}
                </Grid>

                {/* Value Field - Dynamic based on condition type */}
                <Grid item xs={4}>
                    {condition.type && currentConditionType ? (
                        renderField(
                            currentConditionType, // This should have type, label, options, etc.
                            condition.value,
                            (value) => onUpdate({ ...condition, value }),
                            true // isMainCondition
                        )
                    ) : (
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: 'white' }}>Value</InputLabel>
                            <Select size="small" disabled>
                                <MenuItem value="">Select condition first</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                </Grid>

                {/* Toggle + Delete */}
                <Grid item xs={4}>
                    <Stack direction="column" alignItems="center">
                        <Switch
                            checked={condition.isWhitelist}
                            onChange={(e) =>
                                onUpdate({ ...condition, isWhitelist: e.target.checked })
                            }
                            inputProps={{ 'aria-label': 'Include / Exclude toggle' }}
                        />
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption">
                                {condition.isWhitelist ? 'Include' : 'Exclude'}
                            </Typography>
                            <IconButton onClick={onDelete} size="small">
                                <DeleteIcon fontSize="inherit" />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>

            {/* Bottom Row: Subconditions */}
            {condition?.type && currentConditionType?.subConditions?.length > 0 && (
                <Grid container item spacing={2}>
                    {currentConditionType.subConditions.map((sub) => (
                        <Grid item xs={4} key={sub.value}>
                            {renderField(
                                sub,
                                condition.subConditions?.[sub.value],
                                (value) => onUpdate({
                                    ...condition,
                                    subConditions: {
                                        ...condition.subConditions,
                                        [sub.value]: value
                                    }
                                })
                            )}
                        </Grid>
                    ))}
                </Grid>
            )}
        </Grid>
    );
};

const ConditionBuilder = ({ value, onChange, conditionTypes }) => {
    const [conditions, setConditions] = useState(value || []);
    const [globalMode, setGlobalMode] = useState('whitelist');

    const handleAddCondition = (operator) => {
        const newCondition = {
            type: '',
            value: '',
            operator,
            isWhitelist: globalMode === 'whitelist',
        };

        setConditions([...conditions, newCondition]);
        onChange([...conditions, newCondition]);
    };

    const handleUpdateCondition = (index, updatedCondition) => {
        const newConditions = [...conditions];
        newConditions[index] = updatedCondition;
        setConditions(newConditions);
        onChange(newConditions);
    };

    const handleDeleteCondition = (index) => {
        const newConditions = conditions.filter((_, i) => i !== index);
        setConditions(newConditions);
        onChange(newConditions);
    };

    const handleToggleAll = () => {
        const newMode = globalMode === 'whitelist' ? 'blacklist' : 'whitelist';
        setGlobalMode(newMode);

        const updatedConditions = conditions.map((condition) => ({
            ...condition,
            isWhitelist: newMode === 'whitelist',
        }));

        setConditions(updatedConditions);
        onChange(updatedConditions);
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Toggle Button for Whitelist/Blacklist */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'left' }}>
                <ToggleButtonGroup
                    value={globalMode}
                    exclusive
                    onChange={handleToggleAll}
                    aria-label="Toggle Whitelist/Blacklist"
                >
                    <ToggleButton value="whitelist">Include All</ToggleButton>
                    <ToggleButton value="blacklist">Exclude All</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Stack spacing={1}>
                {conditions.map((condition, index) => (
                    <Box key={index}>
                        {index > 0 && (
                            <Typography variant="body2" color="textSecondary" align="left" sx={{ my: 1 }}>
                                {condition.operator}
                            </Typography>
                        )}
                        <ConditionRow
                            condition={condition}
                            onUpdate={(updated) => handleUpdateCondition(index, updated)}
                            onDelete={() => handleDeleteCondition(index)}
                            conditionTypes={conditionTypes}
                        />
                    </Box>
                ))}

                {(conditions.length === 0 || conditions.length > 0) && (
                    <Stack direction="row" spacing={2} justifyContent="left" >
                        <Button
                            variant="outlined"
                            size="small"
                            sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
                            startIcon={<AddIcon color='textPrimary' />}
                            onClick={() => handleAddCondition('AND')}
                        >
                            <Typography color='textPrimary'>
                                AND Condition
                            </Typography>
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
                            startIcon={<AddIcon color='textPrimary' />}
                            onClick={() => handleAddCondition('OR')}
                        >
                            <Typography color='textPrimary'>
                                OR Condition
                            </Typography>
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
};

export default ConditionBuilder;